import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, Tbody, Td, Th, Thead, Tr } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusPill } from "@/components/alert-badge";
import {
  useIncidents,
  type IncidentsListParams,
} from "@/api/queries/incidents";
import { useCurrentUser } from "@/api/queries/me";
import { incidentDisplayName } from "@/api/models";
import { formatDateTime } from "@/lib/format";

export function IncidentsListPage() {
  const me = useCurrentUser();
  // Seed the status filter from the URL so Overview's "Active incidents" tile can
  // deep-link straight to the open list (/incidents?status=open). Anything else
  // falls back to "all".
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const initialStatus: IncidentsListParams["status"] =
    statusParam === "open" || statusParam === "closed" || statusParam === "all"
      ? statusParam
      : "all";
  const [params, setParams] = useState<IncidentsListParams>({
    status: initialStatus,
    page: 1,
    pageSize: 25,
    sort: "dispatched_at_desc",
  });
  const { data, isLoading, isError } = useIncidents(params);
  const tz = me.data?.department?.timezone;
  const totalPages = useMemo(
    () => (data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1),
    [data],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Incidents</h1>
          <p className="text-sm text-muted-foreground">
            Past responses recorded by sensor.
          </p>
        </div>
        <div className="flex gap-2">
          <select
            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
            value={params.status ?? "all"}
            onChange={(e) =>
              setParams((p) => ({
                ...p,
                status: e.target.value as IncidentsListParams["status"],
                page: 1,
              }))
            }
            aria-label="Status filter"
          >
            <option value="all">All statuses</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
          <Input
            type="date"
            className="w-40"
            aria-label="From date"
            onChange={(e) =>
              setParams((p) => ({
                ...p,
                from: e.target.value || undefined,
                page: 1,
              }))
            }
          />
          <Input
            type="date"
            className="w-40"
            aria-label="To date"
            onChange={(e) =>
              setParams((p) => ({
                ...p,
                to: e.target.value || undefined,
                page: 1,
              }))
            }
          />
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {data ? `${data.total.toLocaleString()} incidents` : "Loading…"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
              <Skeleton className="h-6" />
            </div>
          ) : isError ? (
            <p className="p-6 text-sm text-destructive">
              Failed to load incidents.
            </p>
          ) : data && data.items.length > 0 ? (
            <Table>
              <Thead>
                <Tr>
                  <Th>Incident</Th>
                  <Th>Dispatched</Th>
                  <Th>Cleared</Th>
                  <Th>Status</Th>
                  <Th>Monitored FF</Th>
                  <Th>Alerts</Th>
                  <Th className="sr-only">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.items.map((i) => (
                  <Tr key={i.id}>
                    <Td className="font-medium">{incidentDisplayName(i)}</Td>
                    <Td>{formatDateTime(i.dispatched_at, tz)}</Td>
                    <Td>{formatDateTime(i.cleared_at ?? null, tz)}</Td>
                    <Td>
                      <StatusPill status={i.status} />
                    </Td>
                    <Td>{i.sensor_wearer_display_name ?? "—"}</Td>
                    <Td className="tabular-nums">{i.alerts_count ?? 0}</Td>
                    <Td className="text-right">
                      <Link
                        to={`/incidents/${i.id}`}
                        className="text-sm text-primary hover:underline"
                      >
                        Open
                      </Link>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          ) : (
            <p className="p-6 text-sm text-muted-foreground">
              No incidents found.
            </p>
          )}
        </CardContent>
        {data && data.total > data.page_size ? (
          <div className="flex items-center justify-end gap-2 border-t border-border p-3 text-sm">
            <span className="text-muted-foreground">
              Page {data.page} of {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={data.page <= 1}
              onClick={() =>
                setParams((p) => ({
                  ...p,
                  page: Math.max(1, (p.page ?? 1) - 1),
                }))
              }
            >
              Previous
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={data.page >= totalPages}
              onClick={() =>
                setParams((p) => ({ ...p, page: (p.page ?? 1) + 1 }))
              }
            >
              Next
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
